import React, { useEffect, useState } from 'react';
import { Banknote, Building2, Calculator, History, Percent, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatDate } from '../utils/formatters';
import { calculateFeeBreakdown } from '../utils/paymentFees';

const today = () => new Date().toISOString().slice(0, 10);

function formatRule(rateType, rateValue) {
  const value = Number(rateValue || 0);
  return rateType === 'FlatAmount' ? `₱${value.toFixed(2)}` : `${value}%`;
}

function formatMoney(amount) {
  return `₱${Number(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatProviderRule(rule) {
  const fixed = Number(rule.providerFixedFeeAmount || 0);
  return `${formatRule(rule.rateType, rule.rateValue)}${fixed ? ` + ${formatMoney(fixed)}` : ''}`;
}

function formatTierRule(tier) {
  const fixed = tier.rateType === 'Percentage' ? Number(tier.fixedFeeAmount || 0) : 0;
  return `${formatRule(tier.rateType, tier.rateValue)}${fixed ? ` + ${formatMoney(fixed)}` : ''}`;
}

export default function CommissionManagement() {
  const [rates, setRates] = useState([]);
  const [history, setHistory] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [loading, setLoading] = useState(true);
  const [savingMethod, setSavingMethod] = useState(null);
  const [calculatorMethod, setCalculatorMethod] = useState('');
  const [calculatorAmount, setCalculatorAmount] = useState('');
  const [venueAbsorbsFees, setVenueAbsorbsFees] = useState(false);
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    adminService.getVenues('approved').then(setVenues).catch(() => toast.error('Failed to load venues'));
  }, []);

  useEffect(() => {
    loadData(venueId);
  }, [venueId]);

  const loadData = async (selectedVenueId = venueId) => {
    setLoading(true);
    try {
      const data = await adminService.getCommissionHistory(selectedVenueId);
      setRates(data.rates || []);
      setHistory(data.history || []);
    } catch {
      toast.error('Failed to load commission settings');
    } finally {
      setLoading(false);
    }
  };

  const updateRate = (paymentMethod, field, value) => {
    setRates((current) => current.map((rate) => (
      rate.paymentMethod === paymentMethod ? { ...rate, [field]: value } : rate
    )));
  };

  const updateTier = (paymentMethod, tierIndex, field, value) => {
    setRates((current) => current.map((rate) => rate.paymentMethod === paymentMethod
      ? { ...rate, bookingFeeTiers: rate.bookingFeeTiers.map((tier, index) => index === tierIndex ? { ...tier, [field]: value, ...(field === 'rateType' && value === 'FlatAmount' ? { fixedFeeAmount: 0 } : {}) } : tier) }
      : rate));
  };

  const addTier = (paymentMethod) => {
    setRates((current) => current.map((rate) => {
      if (rate.paymentMethod !== paymentMethod) return rate;
      const tiers = rate.bookingFeeTiers || [];
      const previousMaximum = tiers.length ? tiers[tiers.length - 1].maximumAmount : 0;
      return {
        ...rate,
        bookingFeeTiers: [...tiers, {
          minimumAmount: previousMaximum ?? 0,
          maximumAmount: '',
          rateType: 'FlatAmount',
          rateValue: 0,
          fixedFeeAmount: 0,
        }],
      };
    }));
  };

  const removeTier = (paymentMethod, tierIndex) => {
    setRates((current) => current.map((rate) => rate.paymentMethod === paymentMethod
      ? { ...rate, bookingFeeTiers: rate.bookingFeeTiers.filter((_, index) => index !== tierIndex) }
      : rate));
  };

  const saveRule = async (rule) => {
    const transactionFee = Number(rule.rateValue);
    const vatRate = Number(rule.providerFeeVatRate);
    const fixedFee = Number(rule.providerFixedFeeAmount || 0);
    if (!Number.isFinite(transactionFee) || transactionFee < 0 || (rule.rateType === 'Percentage' && transactionFee > 100)) {
      toast.error(rule.rateType === 'Percentage' ? 'Enter a percentage between 0 and 100' : 'Enter a valid peso amount');
      return;
    }
    if (!Number.isFinite(fixedFee) || fixedFee < 0 || Math.abs(fixedFee * 100 - Math.round(fixedFee * 100)) > 0.000001) {
      toast.error('Enter a non-negative fixed provider fee with at most two decimal places');
      return;
    }
    if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      toast.error('Enter a VAT percentage between 0 and 100');
      return;
    }
    const tiers = (rule.bookingFeeTiers || []).map((tier) => ({
      minimumAmount: Number(tier.minimumAmount),
      maximumAmount: tier.maximumAmount === '' || tier.maximumAmount == null ? null : Number(tier.maximumAmount),
      rateType: tier.rateType,
      rateValue: Number(tier.rateValue),
      fixedFeeAmount: tier.rateType === 'Percentage' ? Number(tier.fixedFeeAmount || 0) : 0,
    })).sort((left, right) => left.minimumAmount - right.minimumAmount);
    if (tiers.length === 0) {
      toast.error('Add at least one convenience fee range');
      return;
    }
    for (let index = 0; index < tiers.length; index += 1) {
      const tier = tiers[index];
      const invalidFixed = !Number.isFinite(tier.fixedFeeAmount) || tier.fixedFeeAmount < 0 ||
        Math.abs(tier.fixedFeeAmount * 100 - Math.round(tier.fixedFeeAmount * 100)) > 0.000001;
      const invalidValue = !Number.isFinite(tier.rateValue) || tier.rateValue < 0 || (tier.rateType === 'Percentage' && tier.rateValue > 100);
      const invalidRange = !Number.isFinite(tier.minimumAmount) || tier.minimumAmount < 0 ||
        (tier.maximumAmount != null && (!Number.isFinite(tier.maximumAmount) || tier.maximumAmount <= tier.minimumAmount));
      const overlapsPrevious = index > 0 && (tiers[index - 1].maximumAmount == null || tier.minimumAmount < tiers[index - 1].maximumAmount);
      if (invalidFixed || invalidValue || invalidRange || overlapsPrevious) {
        toast.error('Check the convenience fee ranges. Ranges cannot overlap and each maximum must exceed its minimum.');
        return;
      }
    }
    if (!effectiveDate) {
      toast.error('Select an effective date');
      return;
    }

    setSavingMethod(rule.paymentMethod);
    try {
      await adminService.setCommissionRate({
        venueId: venueId || null,
        paymentMethod: rule.paymentMethod,
        rateType: rule.rateType,
        rateValue: transactionFee,
        bookingFeeRateType: tiers[0].rateType,
        bookingFeeRateValue: tiers[0].rateValue,
        providerFeeVatRate: vatRate,
        providerFixedFeeAmount: fixedFee,
        bookingFeeTiers: tiers,
      }, effectiveDate);
      toast.success(`${rule.label} fees updated`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update commission rule');
    } finally {
      setSavingMethod(null);
    }
  };

  const useGlobalSettings = async () => {
    if (!venueId) return;
    setResetting(true);
    try {
      await adminService.useGlobalCommissionRates(venueId);
      toast.success('Venue now uses global fee settings');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to restore global settings');
    } finally {
      setResetting(false);
    }
  };

  const selectedCalculatorRule = rates.find((rate) => rate.paymentMethod === calculatorMethod) || rates[0];
  const rentalAmount = Number(calculatorAmount);
  const hasCalculatorAmount = calculatorAmount !== '' && Number.isFinite(rentalAmount) && rentalAmount >= 0;
  const { matchingTier, bookingFee, customerConvenienceFee, customerTotal, providerFee, providerFeeVat, providerCost, grossProfit } =
    calculateFeeBreakdown(hasCalculatorAmount ? rentalAmount : 0, selectedCalculatorRule || {}, venueAbsorbsFees);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-admin-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1F2937]">Payment Fee Settings</h1>
        <p className="text-sm text-[#667085] mt-1">Convenience fees are added to the customer charge. PayMongo fees and VAT are internal costs used for revenue, profit, and tax reporting.</p>
      </div>

      <section className="admin-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex-1">
            <label className="admin-label" htmlFor="commission-venue"><span className="flex items-center gap-1.5"><Building2 size={15}/> Settings scope</span></label>
            <select id="commission-venue" value={venueId} onChange={(event) => setVenueId(event.target.value)} className="admin-input mt-1">
              <option value="">Global settings — all venues</option>
              {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name} — {venue.city}</option>)}
            </select>
            <p className="mt-2 text-xs text-[#667085]">Venue-specific rules override the global rule. Any payment method without an override continues to use the global setting.</p>
          </div>
          {venueId && <button type="button" onClick={useGlobalSettings} disabled={resetting} className="admin-btn-secondary h-11"><span className="flex items-center gap-2"><RotateCcw size={16}/>{resetting ? 'Restoring…' : 'Use global settings'}</span></button>}
        </div>
      </section>

      <section className="admin-card">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[#E0F7F4] p-3 text-[#0B7F83]"><Calculator size={20} /></div>
          <div>
            <h2 className="text-base font-semibold text-[#1F2937]">Fee calculator</h2>
            <p className="mt-1 text-sm text-[#667085]">Preview the customer charge and FREAC fee breakdown using the current payment-method rules.</p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <label className="admin-label" htmlFor="calculator-payment-method">Payment method</label>
            <select
              id="calculator-payment-method"
              value={selectedCalculatorRule?.paymentMethod || ''}
              onChange={(event) => setCalculatorMethod(event.target.value)}
              className="admin-input"
            >
              {rates.map((rule) => <option key={rule.paymentMethod} value={rule.paymentMethod}>{rule.label}</option>)}
            </select>
          </div>
          <div>
            <label className="admin-label" htmlFor="calculator-amount">Booking amount (₱)</label>
            <input
              id="calculator-amount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={calculatorAmount}
              onChange={(event) => setCalculatorAmount(event.target.value)}
              placeholder="Enter booking amount"
              className="admin-input"
            />
          </div>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-[#344054]">
          <input type="checkbox" checked={venueAbsorbsFees} onChange={(event) => setVenueAbsorbsFees(event.target.checked)} />
          Venue absorbs the convenience fee
        </label>

        {calculatorAmount !== '' && !hasCalculatorAmount && <p className="mt-3 text-sm text-red-600">Enter a valid booking amount of ₱0 or more.</p>}

        {hasCalculatorAmount && selectedCalculatorRule && (
          <div className="mt-5 overflow-hidden rounded-xl border border-[#E2E8F0]">
            <div className="flex items-center justify-between bg-[#F9FAFB] px-4 py-3">
              <span className="text-sm font-semibold text-[#344054]">{selectedCalculatorRule.label} breakdown</span>
              <span className="text-xs text-[#667085]">{matchingTier ? `Convenience fee: ${formatTierRule(matchingTier)}` : 'No matching convenience fee tier'}</span>
            </div>
            <dl className="divide-y divide-[#EAECF0] text-sm">
              <div className="flex items-center justify-between gap-4 px-4 py-3"><dt className="text-[#667085]">Booking amount</dt><dd className="font-medium text-[#1F2937]">{formatMoney(rentalAmount)}</dd></div>
              <div className="flex items-center justify-between gap-4 px-4 py-3"><dt className="text-[#667085]">Convenience fee charged to customer</dt><dd className="font-medium text-[#1F2937]">{formatMoney(customerConvenienceFee)}</dd></div>
              {venueAbsorbsFees && <div className="flex items-center justify-between gap-4 px-4 py-3"><dt className="text-[#667085]">Convenience fee deducted from venue payout</dt><dd className="font-medium text-[#1F2937]">{formatMoney(bookingFee)}</dd></div>}
              <div className="flex items-center justify-between gap-4 bg-[#F0FAF9] px-4 py-3"><dt className="font-semibold text-[#285B63]">Total customer charge</dt><dd className="font-semibold text-[#0B7F83]">{formatMoney(customerTotal)}</dd></div>
              <div className="flex items-center justify-between gap-4 px-4 py-3"><dt className="text-[#667085]">PayMongo transaction fee (including fixed fee)</dt><dd className="font-medium text-[#1F2937]">−{formatMoney(providerFee)}</dd></div>
              <div className="flex items-center justify-between gap-4 px-4 py-3"><dt className="text-[#667085]">VAT on PayMongo fee</dt><dd className="font-medium text-[#1F2937]">−{formatMoney(providerFeeVat)}</dd></div>
              <div className="flex items-center justify-between gap-4 bg-[#F9FAFB] px-4 py-3"><dt className="font-semibold text-[#344054]">FREAC gross fee profit</dt><dd className={`font-semibold ${grossProfit < 0 ? 'text-red-600' : 'text-[#166534]'}`}>{formatMoney(grossProfit)}</dd></div>
            </dl>
            <p className="border-t border-[#EAECF0] px-4 py-3 text-xs text-[#667085]">PayMongo cost: {formatMoney(providerCost)}. Percentage applies to the total customer charge; the fixed fee applies once per payment, before VAT. Venue fee settings can change the actual customer charge and venue payout for a booking.</p>
          </div>
        )}
      </section>

      <div className="admin-card">
        <div className="flex flex-col gap-3 border-b border-[#EAECF0] pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#1F2937]">Payment method fees</h2>
            <p className="mt-1 text-sm text-[#667085]">Shared boundaries use the lower range: ₱500 uses the ₱0–₱500 tier, while ₱500.01 uses the next tier.</p>
          </div>
          <div className="w-full sm:w-48">
            <label className="admin-label">Effective date</label>
            <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} className="admin-input" />
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {rates.map((rule) => {
            const isTransactionFlat = rule.rateType === 'FlatAmount';
            const isSaving = savingMethod === rule.paymentMethod;
            return (
              <div key={rule.paymentMethod} className="rounded-xl border border-[#EAECF0] p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {isTransactionFlat ? <Banknote size={18} className="text-admin-600" /> : <Percent size={18} className="text-admin-600" />}
                      <h3 className="font-semibold text-[#1F2937]">{rule.label}</h3>
                      {rule.isInherited && <span className="admin-badge-info text-xs">Using global</span>}
                      {!venueId && rule.isDefault && <span className="admin-badge-info text-xs">Built-in default</span>}
                    </div>
                    <p className="mt-1 text-xs text-[#667085]">{rule.description}</p>
                    <p className="mt-2 text-sm font-medium text-admin-700">
                      PayMongo: {formatProviderRule(rule)} · {Number(rule.providerFeeVatRate || 0)}% VAT on the fee · {(rule.bookingFeeTiers || []).length} convenience fee tier{(rule.bookingFeeTiers || []).length === 1 ? '' : 's'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:w-[620px] lg:grid-cols-4 lg:items-end">
                    <div>
                      <label className="admin-label">PayMongo calculation</label>
                      <select
                        value={rule.rateType}
                        onChange={(e) => updateRate(rule.paymentMethod, 'rateType', e.target.value)}
                        className="admin-input"
                      >
                        <option value="Percentage">Percentage (%)</option>
                        <option value="FlatAmount">Flat amount (₱)</option>
                      </select>
                    </div>
                    <div>
                      <label className="admin-label">{isTransactionFlat ? 'Amount (₱)' : 'Rate (%)'}</label>
                      <input
                        type="number"
                        min="0"
                        max={isTransactionFlat ? undefined : '100'}
                        step="0.001"
                        value={rule.rateValue}
                        onChange={(e) => updateRate(rule.paymentMethod, 'rateValue', e.target.value)}
                        className="admin-input"
                      />
                    </div>
                    <div>
                      <label className="admin-label">Fixed fee per transaction (₱)</label>
                      <input
                        type="number" min="0" step="0.01"
                        value={rule.providerFixedFeeAmount ?? 0}
                        onChange={(e) => updateRate(rule.paymentMethod, 'providerFixedFeeAmount', e.target.value)}
                        className="admin-input"
                      />
                    </div>
                    <div>
                      <label className="admin-label">VAT on PayMongo fee (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={rule.providerFeeVatRate}
                        onChange={(e) => updateRate(rule.paymentMethod, 'providerFeeVatRate', e.target.value)}
                        className="admin-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 border-t border-[#EAECF0] pt-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div><p className="text-sm font-semibold text-[#344054]">Convenience fee ranges</p><p className="text-xs text-[#667085]">Leave maximum empty for an open-ended final tier. Percentage ranges can add a fixed fee once per payment.</p></div>
                    <button type="button" onClick={() => addTier(rule.paymentMethod)} className="admin-btn-secondary px-3 py-2 text-xs"><span className="flex items-center gap-1"><Plus size={14} /> Add range</span></button>
                  </div>
                  <div className="space-y-2">
                    {(rule.bookingFeeTiers || []).map((tier, tierIndex) => (
                      <div key={`${rule.paymentMethod}-${tierIndex}`} className="grid grid-cols-2 gap-2 rounded-lg bg-[#F9FAFB] p-3 md:grid-cols-[1fr_1fr_1.2fr_1fr_1fr_auto] md:items-end">
                        <div><label className="admin-label">Minimum (₱)</label><input type="number" min="0" step="0.01" value={tier.minimumAmount} onChange={(e) => updateTier(rule.paymentMethod, tierIndex, 'minimumAmount', e.target.value)} className="admin-input" /></div>
                        <div><label className="admin-label">Maximum (₱)</label><input type="number" min="0" step="0.01" value={tier.maximumAmount ?? ''} placeholder="No limit" onChange={(e) => updateTier(rule.paymentMethod, tierIndex, 'maximumAmount', e.target.value)} className="admin-input" /></div>
                        <div><label className="admin-label">Fee calculation</label><select value={tier.rateType} onChange={(e) => updateTier(rule.paymentMethod, tierIndex, 'rateType', e.target.value)} className="admin-input"><option value="FlatAmount">Flat amount (₱)</option><option value="Percentage">Percentage (%)</option></select></div>
                        <div><label className="admin-label">{tier.rateType === 'FlatAmount' ? 'Fee (₱)' : 'Rate (%)'}</label><input type="number" min="0" max={tier.rateType === 'Percentage' ? '100' : undefined} step="0.001" value={tier.rateValue} onChange={(e) => updateTier(rule.paymentMethod, tierIndex, 'rateValue', e.target.value)} className="admin-input" /></div>
                        <div>
                          {tier.rateType === 'Percentage' && <>
                            <label className="admin-label">Fixed fee per transaction (₱)</label>
                            <input type="number" min="0" step="0.01" value={tier.fixedFeeAmount ?? 0}
                              onChange={(e) => updateTier(rule.paymentMethod, tierIndex, 'fixedFeeAmount', e.target.value)}
                              className="admin-input" />
                          </>}
                        </div>
                        <button type="button" onClick={() => removeTier(rule.paymentMethod, tierIndex)} className="mb-1 rounded-lg p-2 text-red-500 hover:bg-red-50" aria-label="Remove convenience fee range"><Trash2 size={17} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end"><button type="button" onClick={() => saveRule(rule)} disabled={isSaving} className="admin-btn-primary h-11">{isSaving ? 'Saving...' : <span className="flex items-center gap-2"><Save size={16} /> Save {rule.label}</span>}</button></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="admin-card">
        <h2 className="text-base font-semibold text-[#1F2937] flex items-center gap-2">
          <History size={18} className="text-admin-600" /> {venueId ? 'Venue override history' : 'Global fee rule history'}
        </h2>
        {history.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#667085]">No custom payment fee rules have been saved yet.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {history.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between gap-4 rounded-lg bg-[#F9FAFB] p-3">
                <div>
                  <p className="text-sm font-medium text-[#1F2937]">{rule.paymentMethod}</p>
                  <p className="text-xs text-[#667085]">Effective {formatDate(rule.effectiveFrom)}</p>
                </div>
                <span className="text-right text-sm font-semibold text-admin-700">PayMongo {formatProviderRule(rule)} · {Number(rule.providerFeeVatRate || 0)}% VAT on the fee<br />{(rule.bookingFeeTiers || []).map((tier) => `₱${Number(tier.minimumAmount).toFixed(2)}–${tier.maximumAmount == null ? '∞' : `₱${Number(tier.maximumAmount).toFixed(2)}`}: ${formatTierRule(tier)}`).join(' · ') || 'Legacy convenience fee'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
